"use client";
import { logout } from "@/app/(my-app)/auth/actions";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toKebabCase } from "@/lib/utils";
import { Professional_User, User as UserType } from "@/types/types";
import {
  Baby,
  Book,
  Calculator,
  Calendar,
  ChevronDown,
  Cookie,
  Droplets,
  Drumstick,
  Dumbbell,
  FileText,
  FolderOpenDot,
  LayoutDashboard,
  LogIn,
  LogOut,
  Ruler,
  Scale,
  Thermometer,
  User,
  Users,
  Utensils,
  Weight,
} from "lucide-react";
import Link from "next/link";
import { useState } from "react";

interface MobileNavProps {
  user: (UserType & { professional_users: Professional_User }) | null;
}

export function MobileNav({ user }: MobileNavProps) {
  const linkToUser = toKebabCase(
    user?.professional_users?.full_name || user?.full_name || ""
  );
  const isProfesional = user?.role === "profesional";
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);

  return (
    <nav className="flex flex-col justify-between space-y-4 mt-4">
      {/* Contenido solo para usuarios autenticados */}
      {user && (
        <>
          <Link href="/mi-cuenta">
            <Button
              variant="permanentLink"
              className="w-full justify-start transition-colors"
            >
              <User className="mr-2 h-4 w-4" />
              Mi cuenta
            </Button>
          </Link>

          <Link
            href={
              isProfesional
                ? `/nutricionista-dietista/${linkToUser}`
                : "/perfil"
            }
          >
            <Button
              variant="permanentLink"
              className="w-full justify-start transition-colors"
            >
              <User className="mr-2 h-4 w-4" />
              Ver Perfil
            </Button>
          </Link>
        </>
      )}

      {/* Panel para profesionales - solo visible si es profesional */}
      {isProfesional && (
        <Collapsible open={isPanelOpen} onOpenChange={setIsPanelOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="permanentLink"
              className="w-full justify-between transition-colors"
            >
              <span className="flex items-center">
                <LayoutDashboard className="mr-2 h-4 w-4" />
                Panel Profesional
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  isPanelOpen ? "rotate-180" : ""
                }`}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-2">
            <Link href="/dashboard">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <LayoutDashboard className="mr-2 h-4 w-4" />
                Panel de Control
              </Button>
            </Link>
            <Link href="/dashboard/formularios">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <FileText className="mr-2 h-4 w-4" />
                Formularios
              </Button>
            </Link>
            <Link href="/dashboard/clientes">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Users className="mr-2 h-4 w-4" />
                Clientes
              </Button>
            </Link>
            <Link href="/dashboard/dietas">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <FolderOpenDot className="mr-2 h-4 w-4" />
                Dietas
              </Button>
            </Link>
            <Link href="/dashboard/calendario">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Calendario
              </Button>
            </Link>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Sección de calculadoras - disponible para todos */}
      <Collapsible open={isCalculatorOpen} onOpenChange={setIsCalculatorOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="permanentLink"
            className="w-full justify-between transition-colors"
          >
            <span className="flex items-center">
              <Calculator className="mr-2 h-4 w-4" />
              Calculadoras
            </span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${
                isCalculatorOpen ? "rotate-180" : ""
              }`}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-4">
          <Link href="/calculadora">
            <Button
              variant="ghost"
              className="w-full justify-start pl-6 transition-colors font-semibold"
            >
              <Calculator className="mr-2 h-4 w-4" />
              Todas las calculadoras
            </Button>
          </Link>

          {/* Categoría: Básicas */}
          <div className="space-y-2">
            <div className="pl-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Básicas
            </div>
            <Link href="/calculadora/calorias">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Calculator className="mr-2 h-4 w-4" />
                Calculadora de calorías
              </Button>
            </Link>
            <Link href="/calculadora/indice-masa-corporal">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Ruler className="mr-2 h-4 w-4" />
                Calculadora de IMC
              </Button>
            </Link>
            <Link href="/calculadora/peso-ideal">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Weight className="mr-2 h-4 w-4" />
                Peso ideal
              </Button>
            </Link>
          </div>

          {/* Categoría: Nutricionales */}
          <div className="space-y-2">
            <div className="pl-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Nutricionales
            </div>
            <Link href="/calculadora/equivalencia-alimentos-crudos-cocidos">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Utensils className="mr-2 h-4 w-4" />
                Equivalencia de alimentos
              </Button>
            </Link>
            <Link href="/calculadora/macros">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Calculator className="mr-2 h-4 w-4" />
                Calculadora de macros
              </Button>
            </Link>
            <Link href="/calculadora/proteinas">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Drumstick className="mr-2 h-4 w-4" />
                Calculadora de proteínas
              </Button>
            </Link>
            <Link href="/calculadora/agua">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Droplets className="mr-2 h-4 w-4" />
                Calculadora de agua
              </Button>
            </Link>
          </div>

          {/* Categoría: Composición Corporal */}
          <div className="space-y-2">
            <div className="pl-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Composición Corporal
            </div>
            <Link href="/calculadora/tasa-metabolica-basal">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Thermometer className="mr-2 h-4 w-4" />
                Tasa metabólica basal
              </Button>
            </Link>
            <Link href="/calculadora/porcentaje-grasa">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Scale className="mr-2 h-4 w-4" />
                Porcentaje de grasa
              </Button>
            </Link>
          </div>

          {/* Categoría: Especiales */}
          <div className="space-y-2">
            <div className="pl-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Especiales
            </div>
            <Link href="/calculadora/calorias-ejercicio">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Dumbbell className="mr-2 h-4 w-4" />
                Calorías en ejercicio
              </Button>
            </Link>
            <Link href="/calculadora/peso-embarazo">
              <Button
                variant="ghost"
                className="w-full justify-start pl-6 transition-colors"
              >
                <Baby className="mr-2 h-4 w-4" />
                Peso de embarazo
              </Button>
            </Link>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Enlaces comunes - disponibles para todos */}
      <Link href="/blog">
        <Button
          variant="permanentLink"
          className="w-full justify-start transition-colors"
        >
          <Book className="mr-2 h-4 w-4" />
          Blog
        </Button>
      </Link>
      <Link href="/recetas">
        <Button
          variant="permanentLink"
          className="w-full justify-start transition-colors"
        >
          <Cookie className="mr-2 h-4 w-4" />
          Recetas
        </Button>
      </Link>

      {/* Contenido solo para usuarios no autenticados */}
      {!user ? (
        <>
          <Link href="/registro">
            <Button
              variant="permanentLink"
              className="w-full justify-start transition-colors"
            >
              <User className="mr-2 h-4 w-4" />
              Soy Nutricionista o Dietista
            </Button>
          </Link>
          <Link href="/login">
            <Button
              variant="permanentLink"
              className="w-full justify-start transition-colors text-green-primary hover:border-green-primary"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Iniciar sesión
            </Button>
          </Link>
        </>
      ) : (
        <form action={logout} className="w-full">
          <Button
            variant="permanentLink"
            className="w-full justify-start transition-colors"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar Sesión
          </Button>
        </form>
      )}
    </nav>
  );
}
